/**
 * @copyright 2026 Fortee Uniforme Inc. All rights reserved.
 * @license PROPRIETARY - Unauthorized copying prohibited.
 * Protected under the Canadian Copyright Act (R.S.C., 1985, c. C-42).
 */

// single-builder-editor-adapter.js
//
// Satisfies the ADAPTER CONTRACT documented in assets/fortee-logo-editor.js so
// the SINGLE builder can mount the same logo editor as the 2-step builder.
//
// The editor was written against the 2-step's v5.0 artwork model
// (groupArtwork keyed by bundle group, artwork objects carrying versionUrls).
// The single builder instead keeps:
//
//     Alpine.store('builder').perViewArtwork[view] =
//         { url, originalUrl, bgRemovedUrl, trimmedDisplayUrl? }
//
// which is a SUBSET - no versionUrls, no *CroppedUrl, no editorJSON. That is
// workable because the editor's URL resolvers already degrade gracefully:
// _editorBaseUrl() and editorCurrentUrl() both fall back to
// bgRemovedUrl -> url when versionUrls is absent. This adapter therefore maps the
// single builder's model onto the contract and ADDS the few fields the editor
// needs to persist (editorJSON, versionUrls).
//
// Merged LAST into the component (see single-builder.js) so it wins over any
// same-named key.

(function () {
  'use strict';

  function bStore() {
    return (window.Alpine && typeof window.Alpine.store === 'function')
      ? window.Alpine.store('builder')
      : null;
  }

  // 'model' is a browse-only view - artwork can never live there, mirroring
  // _storePerViewArtwork's own guard.
  function realView(v) {
    return (!v || v === 'model') ? 'front' : v;
  }

  window.forteeSingleEditorAdapter = function forteeSingleEditorAdapter() {
    return {

      // ---- editor state the single builder does not otherwise declare -------
      // Per-view, exactly like the 2-step, so the editor's _ensureEditorState()
      // can lazily fill them in.
      editorBg: {},
      colorDirective: {},
      _whiteOutlineOnDark: false,
      _mockup2Cache: {},

      // ---- artwork store ----------------------------------------------------
      // The editor reads and writes `groupArtwork[view]`. In the single builder
      // the equivalent store IS perViewArtwork, so expose it under that name.
      // A getter (not a copy) keeps the editor writing through to the real store
      // instead of mutating a detached object.
      get groupArtwork() {
        var s = bStore();
        return (s && s.perViewArtwork) || {};
      },

      // ---- view / product resolution ---------------------------------------
      // The single builder has exactly one product, so productId is ignored.
      _activeViewForProduct: function () {
        return realView(this.activeView);
      },

      // { product, color, artwork } for the view being edited.
      _activeArtworkContext: function (productId, viewOverride) {
        var s = bStore();
        if (!s) return null;
        var view = realView(viewOverride || this.activeView);
        var art = (s.perViewArtwork || {})[view] || null;
        if (!art) return null;
        return {
          product: (window.productData || null),
          color: this.selectedColor || null,
          artwork: art,
          view: view
        };
      },

      _artworkUrlFromObj: function (a) {
        if (!a) return null;
        return a.url || a.bgRemovedUrl || a.originalUrl || null;
      },

      // ---- version selection ------------------------------------------------
      // Mirrors the 2-step: the logo renders as the bg-removed original on every
      // garment UNLESS the customer opted into the white outline, in which case
      // dark garments get the outline version.
      _autoVersionKey: function (product, color, artwork) {
        if (!this._whiteOutlineOnDark) return 'original';
        var hex = this.step2_getColorHex(product, color);
        return this._isLightHex(hex) ? 'original' : 'outlineDark';
      },

      _isLightHex: function (hex) {
        var s = String(hex || '').trim().replace(/^#/, '');
        if (s.length === 3) s = s.split('').map(function (c) { return c + c; }).join('');
        if (s.length !== 6) return true;
        var r = parseInt(s.slice(0, 2), 16),
            g = parseInt(s.slice(2, 4), 16),
            b = parseInt(s.slice(4, 6), 16);
        if (isNaN(r) || isNaN(g) || isNaN(b)) return true;
        return ((0.2126 * r + 0.7152 * g + 0.0722 * b) / 255) > 0.5;
      },

      // Build the colour versions for the current artwork via the standalone
      // engine (window.forteeLogoVersions). Resolves either way so a failure
      // never blocks the editor - the base logo still renders.
      // applyScope()/_openScope call this with a VIEW argument, so it must honour
      // it rather than always using the on-screen view — otherwise versions get
      // built for the wrong garment view.
      _generateVersions: function (viewOverride) {
        var self = this;
        var ctx = this._activeArtworkContext(null, viewOverride);
        var art = ctx && ctx.artwork;
        var view = ctx && ctx.view;
        var src = art && self._versionSourceUrl(art);
        if (!src || !window.forteeLogoVersions || !window.forteeLogoVersions.buildVersions) {
          return Promise.resolve(null);
        }
        if (art.versionUrls && art.versionUrls.outlineDark) return Promise.resolve(art.versionUrls);

        // One build per distinct source logo, guarding against concurrent
        // duplicate uploads (each upload costs an Uploadcare op).
        self._outlineGenFor = self._outlineGenFor || {};
        if (self._outlineGenFor[src]) return Promise.resolve(null);
        self._outlineGenFor[src] = true;
        self._whiteOutlineBuilding = true;

        var pubkey = (window.productData && window.productData.uploadcarePubkey) ||
          (window.forteeShared && window.forteeShared._uploadcarePubkey) || '';

        return window.forteeLogoVersions.buildVersions(src, {}).then(function (res) {
          // buildVersions returns { source, analysis, versions } where versions.*
          // are raw CANVAS data URLs — they are NOT CDN links and cannot be used
          // directly (the cart and mockups need short https URLs, and the
          // storefront CSP blocks fetching data: URLs). Upload the one we need.
          var v = (res && res.versions) || {};
          var urls = { original: src };   // bg-removed logo is already on the CDN
          if (!v.outlineDark || !window.forteeShared || !window.forteeShared.uploadToUploadcare) {
            return urls;
          }
          return window.forteeShared.uploadToUploadcare(v.outlineDark, pubkey).then(function (cdn) {
            if (cdn) urls.outlineDark = cdn;
            return urls;
          }).catch(function () { return urls; });
        }).then(function (urls) {
          delete self._outlineGenFor[src];
          self._whiteOutlineBuilding = false;
          // Only write back if the artwork for this view has not changed underneath.
          var now = self._activeArtworkContext(null, view);
          var stillSame = now && now.artwork &&
            self._versionSourceUrl(now.artwork) === src;
          if (stillSame) self._patchArtwork(view, { versionUrls: urls });
          // If the editor is previewing a dark garment, re-resolve the base logo
          // so the freshly built outline shows on the canvas immediately.
          // NOTE: deliberately NOT gated on _editorGarmentBg. The customer has
          // usually not picked a garment chip yet, and the 2-step's version of
          // this guard meant the canvas never refreshed — the outline finished
          // building and nothing visibly happened. _toneForHex(null) resolves to
          // 'medium', which _versionKeyForTone treats as dark, so the outline is
          // shown straight away.
          if (self._editorOpen) {
            self._editorVersionKey = self._versionKeyForTone(self._toneForHex(self._editorGarmentBg));
            self._swapBaseLogo(true);
          }
          return urls;
        }).catch(function (e) {
          delete self._outlineGenFor[src];
          self._whiteOutlineBuilding = false;
          console.warn('[FORTEE single] outline version build failed:', e);
          return null;
        });
      },

      // ---- white outline on dark garments -----------------------------------
      // setWhiteOutline / _outlineShowPrep live in bundle-builder-2step.js — they
      // sit just ABOVE the extracted range, so they never came across with the
      // editor. The shared snippet still binds to them, so on the single builder
      // ticking the checkbox called an undefined method and nothing happened.
      _whiteOutlineBuilding: false,

      setWhiteOutline: function (on) {
        var self = this;
        this._whiteOutlineOnDark = !!on;
        this._mockup2Cache = {};

        if (this._whiteOutlineOnDark) {
          // Hold the "Preparing outline..." indicator briefly so the feedback is
          // always perceptible, even when the version is cached or builds fast.
          this._outlineMinHold = true;
          setTimeout(function () { self._outlineMinHold = false; }, 1000);

          // Build once per distinct source logo across all views.
          var store = this.groupArtwork || {};
          var seen = {};
          Object.keys(store).forEach(function (view) {
            var a = store[view];
            if (!a) return;
            var src = a.bgRemovedUrl || a.url;
            if (!src || seen[src]) return;
            seen[src] = 1;
            self._generateVersions(view);
          });
        }

        this._buildPrePlacedTiles();

        // Re-resolve the on-canvas base logo so toggling is visible immediately
        // rather than only after the version finishes uploading.
        // Same reasoning as in _generateVersions: refresh the canvas even when no
        // garment chip is selected, so toggling is immediately visible.
        if (this._editorOpen) {
          this._editorVersionKey = this._versionKeyForTone(this._toneForHex(this._editorGarmentBg));
          this._swapBaseLogo(true);
        }
      },

      _outlineBuilding: function () {
        return !!this._whiteOutlineBuilding;
      },

      _outlineShowPrep: function () {
        return !!this._outlineMinHold || this._outlineBuilding();
      },

      // Merge fields into perViewArtwork[view], replacing the object so Alpine
      // sees the change.
      _patchArtwork: function (view, patch) {
        var s = bStore();
        if (!s) return;
        view = realView(view || this.activeView);
        var cur = (s.perViewArtwork || {})[view] || {};
        var next = {};
        next[view] = Object.assign({}, cur, patch);
        s.perViewArtwork = Object.assign({}, s.perViewArtwork, next);
      },

      // ---- garment colours (background picker + tone) -----------------------
      // The editor calls these with a `product` argument it got from
      // _activeArtworkContext; the single builder has one product so both just
      // read productData.
      // Same source as the builder's own `allColors` getter, so the background
      // picker offers exactly the garment colours the product actually has.
      // Deliberately NOT `availableColors` — that one filters out colours already
      // added to the order, which would hide them from the preview.
      step2_getColors: function () {
        var d = window.productData || {};
        // Option names are translatable — ask for the ROLE, never the
        // English spelling. See forteeShared.optionRole.
        var colorOpt = window.forteeShared.findOptionByRole(d.options, 'color');
        if (colorOpt) return colorOpt.values || [];
        return Object.keys(d.colorHexMap || {});
      },

      step2_getColorHex: function (product, color) {
        var d = window.productData || {};
        var hex = (d.colorHexMap || {})[color];
        if (hex) return hex;
        var fallback = {
          black: '#000000', white: '#ffffff', navy: '#1b2a4a', grey: '#6b7280',
          gray: '#6b7280', charcoal: '#374151', red: '#dc2626', royal: '#2563eb',
          forest: '#166534', sand: '#d4a574', natural: '#f5f0e8', ivory: '#fffff0'
        };
        return fallback[String(color || '').toLowerCase().trim()] || '#cccccc';
      },

      // ---- host services ----------------------------------------------------
      // The editor calls this._t(); the single builder's _t is a module-scope
      // function, not a component method, so delegate to it.
      // The last-resort branch must also reject Shopify's truthy
      // "Translation missing: …" placeholder, or it beats the fallback.
      _t: function (key, fallback, vars) {
        if (typeof window.forteeT === 'function') return window.forteeT(key, fallback, vars);
        var raw = window.__forteeT && window.__forteeT[key];
        if (typeof raw !== 'string' || raw.indexOf('Translation missing:') === 0) return fallback;
        return raw || fallback;
      },

      // Minimal toast. The 2-step's showToast/dismissToast stayed in that
      // builder (they were outside the extracted range), so the single builder
      // needs its own. Rendered by the markup in main-single-builder.liquid.
      _edToast: { show: false, msg: '', ok: true },
      showToast: function (msg, ok) {
        var self = this;
        this._edToast = { show: true, msg: String(msg || ''), ok: ok !== false };
        clearTimeout(this._edToastT);
        this._edToastT = setTimeout(function () { self.dismissToast(); }, 3200);
      },
      dismissToast: function () {
        this._edToast = { show: false, msg: '', ok: true };
      },

      // Re-render the artwork preview after the editor saves. The single builder
      // has no bundle mockup tiles; nudging perViewArtwork is enough to make the
      // preview canvases re-resolve.
      _buildPrePlacedTiles: function () {
        var s = bStore();
        if (!s) return;
        s.perViewArtwork = Object.assign({}, s.perViewArtwork);
        // Invalidate every cached mockup: applyScope may have rewritten several
        // views at once and does not touch perViewMockup itself. The cache is
        // keyed colour -> view (260819-c9m), and every entry under every colour
        // is stale here, so the whole map goes.
        s.perViewMockup = {};
        window.dispatchEvent(new CustomEvent('fortee-artwork-updated'));
      },

      // "Upload a new logo" - from inside the editor, or from the shared Edit
      // fork. Reuses the existing uploader.
      //
      // The argument used to be dropped on the floor. fortee-logo-editor.js's
      // startUploadNew() passes '__newlogo__', which is what routes the finished
      // upload to the scope dialog instead of silently overwriting the current
      // view, so it has to travel on the event detail.
      //
      // Also dispatches 'open-uploader': uploaderBridge owns the Uploadcare
      // dialog and listens for THAT event, and nothing listens for
      // 'fortee-trigger-upload'. Without it "Upload a new logo" merely closed the
      // editor and left the customer to find the upload button themselves.
      triggerUpload: function (target) {
        var s = bStore();
        if (s) s._forceShowUploader = true;
        var isNewLogo = (target === '__newlogo__');
        window.dispatchEvent(new CustomEvent('fortee-trigger-upload', {
          detail: { target: target || null }
        }));
        window.dispatchEvent(new CustomEvent('open-uploader', {
          detail: { targetView: (target && !isNewLogo) ? realView(target) : null }
        }));
        if (this._editorOpen && typeof this.closeEditor === 'function') this.closeEditor();
      },

      // ---- save path -------------------------------------------------------
      // The scope dialog ("where should this logo apply?") now lives in the shared
      // snippet, so the single builder shows the SAME dialog as the 2-step and the
      // shared _openScope/applyScope run unmodified. The earlier override that
      // applied straight to the edited view has been removed.
      //
      // applyScope() writes artwork but knows nothing about perViewMockup, which
      // is single-builder-only. Without clearing it the cart would ship a mockup
      // still showing the PRE-edit logo, since single-builder.js only regenerates
      // when the entry is missing or a base64 preview.
      // Drop the cached mockup for a view so the cart rebuilds it from the
      // current artwork.
      //
      // The cache is keyed colour -> view (260819-c9m), so this clears the view
      // under EVERY colour. Merging {view: null} at the top level, as it did
      // while the map was keyed by view alone, would now write a phantom colour
      // named "front" and leave every real colour's stale mockup in place.
      _invalidateMockup: function (view) {
        var s = bStore();
        if (!s) return;
        view = realView(view || this.activeView);
        var next = {};
        Object.keys(s.perViewMockup || {}).forEach(function (colorName) {
          var views = Object.assign({}, s.perViewMockup[colorName]);
          views[view] = null;
          next[colorName] = views;
        });
        s.perViewMockup = next;
      },

      // ---- entry point ------------------------------------------------------
      // Opens the shared editor on the current view. Guarded so the button can
      // never throw if the module failed to load.
      // viewOverride lets the per-view artwork rows edit their own view without
      // first switching the hero; falls back to whatever view is on screen.
      openLogoEditor: function (viewOverride) {
        if (typeof this.openEditor !== 'function') {
          console.error('[FORTEE single] fortee-logo-editor.js not loaded');
          return;
        }
        var view = realView(viewOverride || this.activeView);
        var s = bStore();
        if (!s || !(s.perViewArtwork || {})[view]) {
          this.showToast(this._t('editor_no_artwork', 'Upload a logo first.'), false);
          return;
        }

        // editorGarmentChips() in the shared module reads
        // Alpine.store('builder').bundleProducts to build the background swatches.
        // The single builder has no bundle, so publish its one product under that
        // key rather than overriding the method — the shared code then works
        // unchanged and both builders stay on one implementation.
        if (!Array.isArray(s.bundleProducts) || !s.bundleProducts.length) {
          s.bundleProducts = window.productData ? [window.productData] : [];
        }

        this._editorView = view;
        // Pass null so the shared chip builder does NOT filter by product id
        // (that filter exists for bundles with several garments).
        this.openEditor(null);
      },

      // =======================================================================
      // ---- logo pipeline adapter -------------------------------------------
      // =======================================================================
      // Satisfies the ADAPTER CONTRACT in assets/fortee-logo-pipeline.js so the
      // single builder runs the SAME upload -> auto bg removal -> auto crop ->
      // prep overlay -> "Logo added" card flow as the 2-step builder.

      // The 2-step declares these on its own component; the single builder does
      // not, and the shared card / review flow read them.
      _VERSION_LABELS: { original: 'Original', outlineDark: 'White outline' },
      _designerReviewRequested: {},

      // Rows are per VIEW here, so the row key IS the view. productId is ignored.
      _viewForLocKey: function (productId, locKey) {
        return realView(locKey);
      },

      _pipelineArtwork: function (view) {
        var s = bStore();
        return s ? ((s.perViewArtwork || {})[realView(view)] || null) : null;
      },

      // Each view owns its own logo here - there is no primary group to inherit
      // from, so the shared _ctxFor lazy cascade must stay inert.
      _pipelineCascadeSource: function () {
        return null;
      },

      // What the "Your logo is ready" panel previews: the logo just uploaded.
      _pipelinePrimaryArtwork: function () {
        return this._pipelineArtwork(this._prepUploadGroup || this.activeView);
      },

      // THE WRITE SEAM. The pipeline hands over a BUNDLE-shaped artwork object;
      // every existing single-builder consumer (hero, mockup compositor, cart,
      // _ensurePlacementGeometry) expects this builder's own shape. Map, then
      // REPLACE the whole perViewArtwork object - mutating a key in place would
      // be invisible to the $watch at single-builder.js:569, which is exactly
      // what re-fits the logo to the print area.
      _pipelineWriteArtwork: function (view, obj, opts) {
        var s = bStore();
        if (!s || !obj) return;
        var v = realView(view);

        // Bundle `url` stays the ORIGINAL; single-builder `url` is the DISPLAY
        // url, so the bg-removed variant has to win here.
        var mapped = {
          url: obj.bgRemovedUrl || obj.url,
          originalUrl: obj.originalUrl || obj.url,
          bgRemovedUrl: obj.bgRemovedUrl || null
        };

        // The crop. `trimmedDisplayUrl || url` is how this builder resolves what
        // to show and measure, so this is what makes the auto-crop and the
        // fit-to-print-area land.
        //
        // Deliberately NOT falling back to originalCroppedUrl once bgRemovedUrl
        // exists: originalCroppedUrl is cropped but still HAS its background, and
        // between the API returning and the async crop finishing that fallback
        // would flash the background back onto the garment. Leaving it undefined
        // shows the bg-removed uncropped logo for that instant instead.
        var trimmed = obj.bgRemovedUrl
          ? obj.bgRemovedCroppedUrl
          : obj.originalCroppedUrl;
        if (trimmed) mapped.trimmedDisplayUrl = trimmed;

        // `editedFrom` rides along or the cart loses the customer's true upload.
        // The editor writes it straight through the groupArtwork getter, so it
        // lands in perViewArtwork — but this seam REBUILDS the record from a
        // fixed field list, so any later write on that view (a second removal,
        // a crop landing) silently dropped it and the order fell back to
        // recording the edit as its own original. That is order 7659's back
        // placement: the same url in both artwork fields.
        ['filename', 'analysis', 'versionUrls', 'editorJSON', 'editedFrom', 'editsLayerUrl', 'editsSpec',
          'originalCroppedUrl', 'bgRemovedCroppedUrl', 'originalTrim', 'bgRemovedTrim', '_versionsFor'].forEach(function (k) {
          if (obj[k] !== undefined) mapped[k] = obj[k];
        });

        var next = {};
        next[v] = mapped;
        s.perViewArtwork = Object.assign({}, s.perViewArtwork, next);

        // Page-level mirror. removeBg() used to set this and several consumers
        // still read `artworkBgRemovedUrl || artworkUrl` (the zone renderer, the
        // cart-time compositor). Without it they would silently fall back to the
        // original, background and all.
        if (mapped.bgRemovedUrl) s.artworkBgRemovedUrl = mapped.bgRemovedUrl;

        // quiet writes come from render-time getters - never touch caches there.
        if (opts && opts.quiet) return;

        // Drop this view's cached mockup so the cart re-bakes it from the
        // processed logo. Same reasoning as _invalidateMockup above.
        this._invalidateMockup(v);
      },

      // Shared card's delete affordance (USER DECISION 2026-08-07). This is the
      // old uploaderBridge.deleteViewArtwork logic, rewritten to go through the
      // replace-the-object seam and to drop the stale mockup too.
      _pipelineDeleteArtwork: function (view) {
        var s = bStore();
        if (!s) return;
        var v = realView(view);
        var next = {};
        next[v] = null;
        s.perViewArtwork = Object.assign({}, s.perViewArtwork, next);
        this._invalidateMockup(v);
        var pv = s.perViewArtwork || {};
        var hasAny = Object.keys(pv).some(function (k) {
          var a = pv[k];
          return !!(a && (a.url || a.bgRemovedUrl));
        });
        if (!hasAny) {
          s.artworkUrl = null;
          s.artworkBgRemovedUrl = null;
        }
        window.dispatchEvent(new CustomEvent('fortee-artwork-updated'));
      },

      // The views that currently hold artwork, in hero order. The section's
      // x-for over the shared card uses this.
      artRowKeys: function () {
        var s = bStore();
        var pv = (s && s.perViewArtwork) || {};
        var out = [];
        ['front', 'back', 'left', 'right'].forEach(function (v) {
          var a = pv[v];
          if (a && (a.url || a.bgRemovedUrl)) out.push(v);
        });
        return out;
      },

      // Human label for a row. Same resolution the deleted .ftb-artwork-row
      // template inlined, so the eyebrow reads exactly as it did before.
      _rowPlacementLabel: function (productId, view) {
        var s = bStore();
        var v = realView(view);
        var list = (s && s.configuredPlacements) || [];
        var found = null;
        for (var i = 0; i < list.length; i++) {
          if (list[i] && list[i].view === v) { found = list[i]; break; }
        }
        return String((found && found.displayName) || v).toUpperCase();
      },

      // No step machine to advance - "Continue" just closes the overlay.
      _prepAfterContinue: function () {},

      _prepReviewTarget: function () {
        return { pid: null, key: this._prepUploadGroup || realView(this.activeView) };
      },

      // Overrides the shared implementation (this adapter merges LAST). Same
      // bundleProducts seeding openLogoEditor does, and _artworkEditFor stays
      // null so the shared chip builder does not filter by product id.
      openArtworkEditor: function (productId, view) {
        var s = bStore();
        var v = realView(view);
        if (!s || !(s.perViewArtwork || {})[v]) return;
        if (!Array.isArray(s.bundleProducts) || !s.bundleProducts.length) {
          s.bundleProducts = window.productData ? [window.productData] : [];
        }
        this._editorView = v;
        this._artworkEditFor = null;
        this._artworkEditKey = v;
      },

      // True when there is artwork on the current view - gates the Edit button.
      get canEditLogo() {
        var s = bStore();
        var view = realView(this.activeView);
        var a = s && (s.perViewArtwork || {})[view];
        return !!(a && (a.url || a.bgRemovedUrl || a.originalUrl));
      }
    };
  };
})();
